<template>
	<app-page>
		<app-heading title="Bookings">
			<template v-slot:right>
				<app-button
					outlined
					small
					color="secondary"
					icon="fa-sync"
					@click="refresh = true"
				>Refresh</app-button>
				<input-file
					v-if="$ravello.can('booking.post')"
					type="csv"
					button
					small
					outlined
					label="Import From Studeo"
					@load="beforeImport"
				/>
				<app-button
					small
					outlined
					icon="fa-download"
					color="secondary"
					@click="onExport"
				>
					Export
				</app-button>
			</template>
		</app-heading>
		<app-filter :value="hasFilters" @click="applyFilters()">
			<app-row no-padding>
				<app-cell cols="6" md="3">
					<input-text
						v-model="filters.external_id"
						label="External ID"
						clearable
						dense
					></input-text>
				</app-cell>
				<app-cell cols="6" md="3">
					<input-text
						v-model="filters.studeo_id"
						label="Confirmation Number"
						clearable
						dense
					></input-text>
				</app-cell>
				<app-cell cols="6" md="3">
					<input-select
						v-model="filters.integration"
						label="Integration"
						:items="integrations"
						clearable
						dense
					></input-select>
				</app-cell>
				<app-cell cols="6" md="3">
					<input-select
						v-model="filters.resort"
						label="Resort"
						:items="resorts"
						clearable
						dense
					></input-select>
				</app-cell>
				<app-cell cols="6" md="3">
					<input-select
						v-model="filters.status"
						label="Status"
						:items="statuses"
						clearable
						dense
					></input-select>
				</app-cell>
				<app-cell cols="6" md="3">
					<input-select
						v-model="filters.last_action"
						label="Action"
						:items="actions"
						clearable
						dense
					></input-select>
				</app-cell>
				<app-cell cols="6" md="3">
					<input-switch
						v-model="filters.include_quotes"
						dense
						label="Include quotes"
					></input-switch>
				</app-cell>
			</app-row>
		</app-filter>
		<app-row>
			<app-cell>
				<app-data-table
					:items="bookings"
					:total="count"
					:refresh.sync="refresh"
					:headers="headers"
					:sort-by="'last_modified'"
					:sort-desc="true"
					:initial-state="tableState"
					@update="fetchBookings"
				>
					<template v-slot:studeo_id="{ item }">
						<span v-if="item.studeo_id">{{ item.studeo_id }}</span>
						<span v-else class="warning--text">Not defined</span>
					</template>
					<template v-slot:integration="{ item }">
						<div>{{ getIntegrationName(item.integration) }}</div>
						<div>
							<i>{{ item.channel_name || '' }}</i>
						</div>
					</template>
					<template v-slot:unit_code="{ item }">
						<div>{{item.unit_code}}</div>
						<div><i>{{ getResortName(item.studeo_resort) }}</i></div>
					</template>
					<template v-slot:last_action="{ item }">
							<span v-if="item.last_action === 'quote'">Quote</span>
							<span v-if="item.last_action === 'new'">New</span>
							<span v-if="item.last_action === 'update'">Updated</span>
							<span v-if="item.last_action === 'cancel'">Cancelled</span>
							<span v-if="item.last_action === 'requested'">Requested</span>
					</template>
					<template v-slot:status="{ item }">
							<span v-if="item.status === 0" class="warning--text">Pending</span>
							<span v-if="item.status === 1" class="success--text">Success</span>
							<span v-if="item.status === 2" class="error--text">Error</span>
					</template>
					<template v-slot:last_modified="{ item }">
						<div>{{ item.last_modified | datetime }}</div>
						<div><i>{{ item.last_modified | datetime(true) }}</i></div>
					</template>
					<template v-slot:actions="{ item }">
						<app-button
							color="secondary"
							:to="{ name: 'studeo-booking', params: { booking: item._id } }"
							icon="fa-history"
							table
						>
							History
						</app-button>
					</template>
				</app-data-table>
			</app-cell>
		</app-row>

		<app-dialog
			v-model="importDialog.show"
			max-width="320"
			persistent
			title="Import Reservations"
		>
			<app-dialog-body>
				<input-select
					v-model="importDialog.resort"
					label="Resort"
					:items="resorts"
				/>
				<div v-for="integration in integrations" :key="`import-integration-${integration.value}`">
					<input-select
						v-model="importDialog.integrations[integration.value]"
						:label="getIntegrationName(integration.value)"
						:items="importDialog.channels"
						multiple
					/>
				</div>
			</app-dialog-body>
			<app-dialog-footer>
				<app-button
					color="primary"
					:disabled="!importDialog.resort"
					:loading="importDialog.loading"
					@click="onImport"
				>Import</app-button>
				<app-button @click="importDialog.show = false">Cancel</app-button>
			</app-dialog-footer>
		</app-dialog>
	</app-page>
</template>

<script>
import moment from 'moment'

import { filters, tableState } from '@/mixins'
import { mapAll } from '@/utils/async'
import { mapSelectOptions, dynamicNewMapEntries } from '@/utils/maps'
import { exportCollection2 } from '@/utils/import'

export default {
	mixins: [filters, tableState],
	async created() {
		this.resorts = mapSelectOptions(await this.$ravello.request()
			.v(2)
			.to('resorts')
			.field(['name', 'studeo_id'])
			.limit(100)
			.get(), { value: 'studeo_id' })
		this.integrations = mapSelectOptions(await this.$ravello.request()
			.v(2)
			.to('integrations')
			.field(['name'])
			.limit(100)
			.get())

		this.resortsMap = this.resorts.reduce((acc, resort) => ({ ...acc, [resort.value.toString()]: resort.text }), {})
		this.integrationsMap = this.integrations.reduce((acc, integration) => ({ ...acc, [integration.value]: integration.text }), {})
	},
	data() {
		return {
			bookings: [],
			integrationsMap: {},
			resortsMap: {},
			channelsMap: {},
			count: -1,
			refresh: false,
			headers: [{
				text: 'External ID',
				value: 'content.id',
				sortable: false,
			}, {
				text: 'Confirmation Number',
				value: 'studeo_id',
				sortable: true,
			}, {
				text: 'Integration',
				value: 'integration',
				sortable: true,
			}, {
				text: 'Unit',
				value: 'unit_code',
				sortable: true,
			}, {
				text: 'Action',
				value: 'last_action',
				sortable: true,
			}, {
				text: 'Status',
				value: 'status',
				sortable: false,
			}, {
				text: 'Last modified',
				value: 'last_modified',
				sortable: true,
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				sortable: false,
			}],
			resorts: [],
			integrations: [],
			filters: {
				studeo_id: '',
				external_id: '',
				resort: null,
				integration: null,
				status: null,
				last_action: null,
				include_quotes: false,
			},
			importDialog: {
				show: false,
				resort: null,
				channels: [],
				integrations: {},
				data: null,
				loading: false,
			},
			defaultFilters: {
				include_quotes: false,
			},
			defaultTableSort: '-last_modified',
			statuses: [{
				text: 'Pending',
				value: 0,
			}, {
				text: 'Success',
				value: 1,
			}, {
				text: 'Failed',
				value: 2,
			}],
			actions: [{
				text: 'New',
				value: 'new',
			}, {
				text: 'Updated',
				value: 'update',
			}, {
				text: 'Cancelled',
				value: 'cancel',
			}, {
				text: 'Requested',
				value: 'requested',
			}],
		}
	},
	methods: {
		getIntegrationName(id) {
			return this.integrationsMap[id] || 'UNDEFINED'
		},

		getResortName(id) {
			return this.resortsMap[id] || 'UNDEFINED'
		},

		getChannelName(resort, id) {
			return this.channelsMap[resort]
				&& this.channelsMap[resort][id]
				&& this.channelsMap[resort][id].name
				? this.channelsMap[resort][id].name
				: 'UNDEFINED'
		},

		async updateChannelsMap() {
			const split = this.bookings.reduce((acc, booking) => {
				if (!this.resortsMap[booking.studeo_resort]) {
					return acc
				}
				const resortId = booking.studeo_resort
				if (!acc[resortId]) {
					acc[resortId] = []
				}
				acc[resortId].push(booking)
				return acc
			}, {})

			await mapAll(split, async (bookings, resort) => {
				const resortStudeo = bookings[0].studeo_resort
				if (!this.channelsMap[resortStudeo]) {
					this.channelsMap[resortStudeo] = {}
				}

				const res = await dynamicNewMapEntries(
					bookings,
					'studeo_channel',
					this.channelsMap[resortStudeo],
					this.$ravello.request()
						.v(2)
						.to('resorts')
						.to(resort)
						.to('channels')
						.field(['studeo_id', 'name']),
					{ key: 'studeo_id' },
				)

				if (res.err) {
					this.$notifications.pushAlert('error', res.err.data ? res.err.data.message : res.err.message)
				}

				this.channelsMap[resortStudeo] = {
					...this.channelsMap[resortStudeo],
					...res.entries,
				}
			})

			this.bookings = this.bookings.map(booking => ({
				...booking,
				channel_name: this.getChannelName(booking.studeo_resort, booking.studeo_channel),
			}))
		},

		applyFiltersToRequest(request) {
			if (this.appliedFilters.studeo_id) {
				request.param('studeo_id', this.appliedFilters.studeo_id)
			}
			if (this.appliedFilters.external_id) {
				request
					.orParam('content.id', this.appliedFilters.external_id)
					.orParam('content.reservation', this.appliedFilters.external_id)
					.orParam('content.reservation', this.appliedFilters.external_id, 'int')
			}
			if (this.appliedFilters.resort) {
				request.param('studeo_resort', this.appliedFilters.resort)
			}
			if (this.appliedFilters.integration) {
				request.param('integration', this.appliedFilters.integration)
			}
			if (this.appliedFilters.status) {
				request.param('status', this.appliedFilters.status)
			}
			if (this.appliedFilters.last_action) {
				request.param('last_action', this.appliedFilters.last_action)
			}
			if (!this.appliedFilters.include_quotes) {
				request.param('last_action', this.appliedFilters.last_action
					? this.appliedFilters.last_action : ['new', 'update', 'cancel', 'requested'])
			} else if (this.appliedFilters.last_action) {
				request.param('last_action', this.appliedFilters.last_action)
			}
		},

		async fetchBookings(pagination) {
			this.updateTableState(pagination)
			const request = this.$ravello.request()
				.v(2)
				.to('bookings')
				.fromPagination(pagination)
				.fields([
					'studeo_id',
					'integration',
					'last_action',
					'last_modified',
					'status',
					'content',
					'studeo_resort',
					'studeo_channel',
					'unit_code',
				])

			this.applyFiltersToRequest(request)

			try {
				this.bookings = await request.get()
				if (this.count === -1) {
					const count = await request.count()
					this.count = count.count
				}

				await this.updateChannelsMap()
			} catch (err) {
				this.$notifications.pushAlert('error', err.data ? err.data.message : err.message)
			}
		},

		openImportDialog(data) {
			this.importDialog.data = data
			this.importDialog.channels = Object.values(data.reduce((acc, entry) => {
				acc[entry.channel_internal_id] = {
					text: entry.channel,
					value: entry.channel_internal_id,
				}
				return acc
			}, {}))
			this.importDialog.integrations = this.integrations.reduce((acc, integration) => ({ ...acc, [integration.value]: [] }), {})
			this.importDialog.resort = null
			this.importDialog.loading = false
			this.importDialog.show = true
		},

		beforeImport(data) {
			const formattedData = data.reduce((acc, entry) => {
				const newEntry = {}

				Object.keys(entry).forEach((key) => {
					newEntry[key.split(' ').join('_').toLocaleLowerCase()] = entry[key]
				})
				acc.push(newEntry)
				return acc
			}, [])
			this.openImportDialog(formattedData)
		},

		async onImport() {
			this.importDialog.loading = true
			const resort = await this.$ravello.request()
				.v(2)
				.to('resorts')
				.to(this.importDialog.resort)
				.get()

			const channelsMap = Object.keys(this.importDialog.integrations).reduce((acc, integrationId) => {
				const channels = this.importDialog.integrations[integrationId]
				channels.forEach((channel) => {
					acc[channel] = integrationId
				})

				return acc
			}, {})

			const importData = this.importDialog.data
				.filter(entry => !!channelsMap[entry.channel_internal_id])
				.map(entry => ({
					customer: {
						email: entry.customer_email,
						first: entry.customer.split(', ')[1] || '',
						last: entry.customer.split(', ')[0] || '',
						phone: '',
						address: {
							line_1: '',
							city: '',
							country: '',
							zipcode: '',
						},
					},
					notes: [],
					last_action: 'new',
					created: moment(entry.booked_date).toDate(),
					content: {
						id: entry['channel_#'],
						...(entry['channel_#'].split(':').length > 1 ? { reservation: entry['channel_#'].split(':')[0] } : {}),
						...(entry['channel_#'].split('.').length > 1 ? { reservation: entry['channel_#'].split('.')[0] } : {}),
					},
					last_modified: moment(`${entry.booked_date}:00`).toDate(),
					source: entry.channel,
					reservation: {
						adults: 1,
						children: 0,
						pets: 0,
						checkin: entry.arrival_date,
						checkout: entry.departure_date,
					},
					amount: {
						total: parseFloat(entry.total),
						taxes: parseFloat(entry.taxes),
						subtotal: parseFloat(entry.subtotal),
					},
					currency: resort.currency,
					last_request: {},
					unit_code: entry.unit_code,
					studeo_resort: resort.studeo_id,
					status: 1,
					studeo_channel: parseInt(entry.channel_internal_id, 10),
					integration: channelsMap[entry.channel_internal_id],
					history: [{
						action: 'imported',
						processed: moment().toDate(),
						request: { success: true },
					}],
					imported: true,
					last_error: null,
					studeo_hold: null,
					studeo_id: entry['confirmation_#'] && entry['confirmation_#'] !== '' ? entry['confirmation_#'] : entry['folio_#'],
				}))

			await this.$ravello.request()
				.v(2)
				.to('bookings')
				.to('import')
				.create(importData)

			this.count = -1
			this.importDialog.loading = false
			this.importDialog.show = false
		},

		async onExport() {
			const request = this.$ravello.request()
				.v(2)
				.to('bookings')

			this.applyFiltersToRequest(request)
			await exportCollection2('bookings', 'csv', request, {
				map: entry => ({
					confirmationNumber: entry.studeo_id,
					externalConfirmationNumber: entry.content && entry.content.id,
					unitCode: entry.unit_code,
					bookedOn: entry.created,
					checkin: entry.reservation && entry.reservation.checkin,
					checkout: entry.reservation && entry.reservation.checkout,
					adults: entry.reservation && entry.reservation.adults,
					children: entry.reservation && entry.reservation.children,
					pets: entry.reservation && entry.reservation.pets,
					source: entry.source,
					subtotal: entry.amount && entry.amount.subtotal,
					taxesFees: entry.amount && entry.amount.taxes,
					total: entry.amount && entry.amount.total,
					currency: entry.currency,
					guestFirstName: entry.customer && entry.customer.first,
					guestLastName: entry.customer && entry.customer.last,
					guestEmail: entry.customer && entry.customer.email,
					guestPhone: entry.customer && entry.customer.phone,
					status: entry.last_action,
					hasError: entry.last_error ? 'Yes' : 'No',
					studeoResortId: entry.studeo_resort,
					studeoChannelId: entry.studeo_channel,
				}),
			})
		},
	},
}
</script>
